/**
 * UploadQueueContext — persists the bill-upload queue across navigation.
 *
 * User pain (Sprint 2026-07): "when it keeps on scanning images I can't go
 * back to any other screen ... as soon as I click any tab the upload screen
 * starts afresh."
 *
 * Root cause: the queue lived in `useState` inside UploadPage. Navigating
 * to /vendors unmounted UploadPage, blowing away the state. Coming back
 * showed an empty queue.
 *
 * Fix: hoist just the state (queue, running) into a provider mounted at
 * App level. UploadPage keeps ownership of the callbacks + workers — those
 * still update via the provider's setters. The provider never unmounts, so:
 *   • Navigate away: fetch() calls already in-flight continue on the server;
 *                    their .then() handlers keep pushing status updates
 *                    into the (still-alive) provider state.
 *   • Navigate back: UploadPage re-renders reading from context, so the
 *                    user sees exactly the queue they left, live-updated.
 *
 * Note: File objects themselves are NOT persistable across page reloads
 * (they're binary handles into a picker session that ends on refresh). So
 * on hard reload the queue is intentionally cleared. That's acceptable —
 * the primary complaint was intra-session navigation, not F5.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const UploadQueueContext = createContext(null);

export function UploadQueueProvider({ children }) {
    const [queue, setQueue] = useState([]);
    const [running, setRunning] = useState(false);
    // Cross-remount coordination: the worker loop guard must survive
    // UploadPage un/re-mount cycles too, otherwise navigating away and
    // back would kick off a second processQueue pass while the first is
    // still running (double-uploads).
    const runningRef = useRef(false);
    // Shared "always-current queue" ref, kept in sync with `queue` state.
    // Workers read via `queueRef.current` so they see items added by a
    // FUTURE re-mount of UploadPage, not the frozen closure snapshot
    // that would exist if this ref lived inside the page component.
    const queueRef = useRef([]);
    queueRef.current = queue;

    const patchItem = useCallback((id, patch) => {
        setQueue((q) => q.map((it) => (
            it.id === id
                ? { ...it, ...(typeof patch === 'function' ? patch(it) : patch) }
                : it
        )));
    }, []);

    const removeItem = useCallback((id) => {
        setQueue((q) => q.filter((it) => it.id !== id));
    }, []);

    const clearFinished = useCallback(() => {
        setQueue((q) => q.filter((x) => !['done', 'alerts', 'error', 'cancelled', 'duplicate', 'rejected'].includes(x.status)));
    }, []);

    const clearAll = useCallback(() => {
        setQueue([]);
    }, []);

    const addItems = useCallback((items) => {
        if (!items || !items.length) return;
        setQueue((q) => [...q, ...items]);
    }, []);

    const value = useMemo(() => ({
        queue,
        setQueue,
        running,
        setRunning,
        runningRef,
        queueRef,
        patchItem,
        removeItem,
        clearFinished,
        clearAll,
        addItems,
    }), [queue, running, patchItem, removeItem, clearFinished, clearAll, addItems]);

    return (
        <UploadQueueContext.Provider value={value}>
            {children}
        </UploadQueueContext.Provider>
    );
}

export function useUploadQueue() {
    const ctx = useContext(UploadQueueContext);
    if (!ctx) {
        throw new Error('useUploadQueue must be used inside <UploadQueueProvider>');
    }
    return ctx;
}

export default UploadQueueContext;
